<template>
    <div
        :class="[$style['mds-tooltip'], classObject, sizeClass, variationClass, positionClass]"
        role="tooltip"
        :aria-hidden="!visible"
        :data-mds-version="version"
    >
        <slot>{{ text }}</slot>

        <div v-if="variation === 'prompt'" :class="$style['mds-tooltip__close-button']">
            <mds-button
                type="button"
                variation="icon-only"
                size="small"
                icon="remove"
                :text="dismissIconAriaLabel"
                on-dark
                @click="hide"
            />
        </div>
    </div>
</template>

<script>
    import { MdsPositioning, positions } from '@mds/utils-js-vue3';
    import { MdsButton } from '@mds/button-vue3';
    import packageInfo from '../package.json';
    const { name, version } = packageInfo;

    export default {
        name: 'MdsTooltip',
        mixins: [MdsPositioning],
        components: {
            MdsButton,
        },
        props: {
            fixed: {
                type: Boolean,
                default: false,
                docs: {
                    validation: '—',
                    description:
                        'When a tooltip is triggered from an element or container set to `position: fixed;`, this prop must be set `true` to ensure proper positioning.',
                },
            },
            dismissIconAriaLabel: {
                type: String,
                default: 'Dismiss',
                docs: {
                    validation: '—',
                    description: 'Sets the `aria-label` attribute on the icon-only dismiss button.',
                },
            },
            position: {
                type: Array,
                default: () => positions,
                validator: value => value.every(val => positions.includes(val)),
                docs: {
                    validation:
                        'One or more of the following: `bottom-left`, `bottom-center`, `bottom-right`, `left-center`, `right-center`, `top-left`, `top-center`, `top-right`.',
                    description:
                        'If a single value, sets the position of the tooltip. If multiple values, the first value sets the initial position while the rest define the auto-position order.',
                },
            },

            size: {
                type: String,
                default: 'medium',
                validator: value => ['small', 'medium', 'large'].indexOf(value) > -1,
                docs: {
                    validation: 'One of: `small`, `medium`, `large`.',
                    description:
                        'Sets the size of the tooltip. Regardless of size, tooltips have a maximum width of 200px, with text wrapping to fit as necessary.',
                },
            },
            triggeredBy: {
                type: String,
                required: true,
                docs: {
                    validation: '**Required**',
                    description: 'The `id` of the element that triggers the tooltip',
                },
            },
            text: {
                type: String,
                default: '',
                docs: {
                    validation: '**Required**',
                    description:
                        'The text that appears in the tooltip. Can also be passed via the [default slot](#slots).',
                },
            },
            variation: {
                type: String,
                default: 'default',
                validator: value => ['default', 'error', 'prompt'].indexOf(value) > -1,
                docs: {
                    validation: 'One of: `default`, `error`, `prompt`.',
                    description: 'Sets the tooltip variation.',
                },
            },
            visible: {
                type: Boolean,
                default: false,
                docs: {
                    validation: '—',
                    description: 'Sets the visibility of the tooltip.',
                    support: ['v-model:visible'],
                },
            },
        },
        watch: {
            visible(isVisible) {
                isVisible ? this.show() : this.hide();
            },
        },
        data() {
            return {
                currentPosition: this.position[0],
                triggerElement: null,
                version: `${name}-${version}`,
                ready: false,
            };
        },
        computed: {
            classObject() {
                if (!this.ready) {
                    return {};
                }
                const obj = {};
                obj[this.$style['mds-tooltip--visible']] = this.visible;
                obj[this.$style['mds-tooltip--fixed']] = this.fixed;

                return obj;
            },
            sizeClass() {
                return this.$style[`mds-tooltip--${this.size}`];
            },
            variationClass() {
                return this.$style[`mds-tooltip--${this.variation}`];
            },
            positionClass() {
                return this.currentPosition ? this.$style[`mds-tooltip--${this.currentPosition}`] : '';
            },
            autoPosition() {
                return this.position.length > 1;
            },
        },
        emits: ['update:visible', 'mds-tooltip-position-updated'],
        methods: {
            show() {
                this.$nextTick(() => {
                    this.updatePosition();
                    this.$emit('update:visible', true);
                });
            },
            hide() {
                this.$emit('update:visible', false);
            },
            updatePosition() {
                try {
                    const { setPosition, attributes } = this.getPositionAttributes();
                    this.currentPosition = setPosition;
                    this.$el.setAttribute('style', this.formatPositionStyleAttributes(attributes));
                    this.$emit('mds-tooltip-position-updated');
                } catch (e) {
                    console.log(e);
                }
            },
            bindEventHandlers() {
                if (this.triggerElement) {
                    window.addEventListener('resize', this.debounce(this.updatePosition, 50));

                    if (this.autoPosition) {
                        window.addEventListener('scroll', this.debounce(this.updatePosition, 50));
                    }
                }
            },
            removeEventHandlers() {
                if (this.triggerElement) {
                    window.removeEventListener('resize', this.updatePosition, true);
                    if (this.autoPosition) {
                        window.removeEventListener('scroll', this.updatePosition, true);
                    }
                }
            },
        },
        mounted() {
            document.body.appendChild(this.$el);
            this.triggerElement = document.getElementById(`${this.triggeredBy}`);
            this.bindEventHandlers();
            this.updatePosition();

            // wait until element is appending to body and position is updated before showing for the first time
            this.ready = true;

            if (this.visible) {
                this.show();
            }
        },
        beforeUnmount() {
            this.removeEventHandlers();
            if (this.$el.parentNode) {
                this.$el.parentNode.removeChild(this.$el);
            }
        },
    };
</script>

<style lang="scss" module>
    @use './tooltip.scss';
</style>
